// main imports
import React, { Component, createRef } from "react";
import { match, Link } from "react-router-dom";
import { History, Location } from "history";
import { FormattedMessage } from "react-intl";

// backend
import { getBackend as getBackend2 } from "@/lib/backend/Backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types.ts";
import { ILabelValuePairEx, ILabelValuePair, intersect } from "@/lib/Util";
import * as BusinessLogic from "@/lib/BusinessLogic";
import { PEOPLE_TAGS, PEOPLE_TAGS_DEFAULT, PEOPLE_TAGS_ACCESS } from "@/lib/ManufacturingTags.generated";
import { PEOPLE_TAGS as PEOPLE_TAGS_EDITOR } from "@/lib/ManufacturingTagsEditor.generated";

// import subcomponents
import ErrorComponent from "@/components/ErrorComponent";
import { translate } from "@/components/IntlProviderWrapper";
import Toggle from "react-toggle";
import Select from "react-select";
import DayShiftsToggle from "./DayShiftsToggle";

import TagsTables from "@/components/react/TagsTables";
import {
    mapDataToCategories,
    mapDataToCustomTags,
    Columns,
    onChangeUpdateTags,
    type TagsConfig,
    type TagUpdateValue,
} from "@/components/react/ReactTableTags";
import UserDocumentationLink from "@/components/UserDocumentationLink";

import img_banner from "@/img/banner.png";

type ExternalIdCollision = {
    uuid: string;
    name: string;
    plant_uuids: string[];
};

// defining types
type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    active: boolean;
    adjuster: boolean;
    componentTitle: string;
    editFlag: boolean;
    error: string;
    external_id: string;
    external_id_collisions: ExternalIdCollision[];
    external_id_check_pending: boolean;
    external_id_editing: boolean;
    external_id_original: string;
    name: string;
    plants: ILabelValuePair[];
    line_groups: ILabelValuePairEx[];
    plants_choice: t.IPlantData[];
    shift_preference: boolean[];
    success: string;
    tags: t.ITags;
    ted_id: string;
    uuid: string;
    has_enabled_future_shifts: boolean;
};

export type PeopleFormProps = Props;
export type PeopleFormState = State;

const EXTERNAL_ID_CHECK_DEBOUNCE_MS = 1000;

/**
 * Displaying production lines list.
 */
class PeopleCreate extends Component<Props, State> {
    external_id_check_seq = 0;
    external_id_check_timer: ReturnType<typeof setTimeout> | null = null;
    external_id_input_ref = createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);

        // prepare default tags
        const tags: t.ITags = {};
        tags[PEOPLE_TAGS.reduced_worktime] = PEOPLE_TAGS_DEFAULT.reduced_worktime;
        // initialize state
        this.state = {
            uuid: "",
            name: "",
            external_id: "",
            external_id_original: "",
            external_id_editing: false,
            external_id_collisions: [],
            external_id_check_pending: false,
            active: true,
            adjuster: false,
            ted_id: "",
            shift_preference: [false, false, false],
            componentTitle: translate("Manufacturing.People.new_person_details", "New Person Details"),
            error: "",
            success: "",
            editFlag: false,
            tags,
            plants: [],
            plants_choice: [],
            line_groups: [],
            has_enabled_future_shifts: false,
        };
    }

    componentWillUnmount() {
        this.clearExternalIdCheckTimer();
    }

    clearExternalIdCheckTimer = () => {
        if (this.external_id_check_timer !== null) {
            clearTimeout(this.external_id_check_timer);
            this.external_id_check_timer = null;
        }
    };

    invalidateExternalIdCheck = () => {
        this.clearExternalIdCheckTimer();
        this.external_id_check_seq += 1;
    };

    /**
     * Loading person data.
     */
    async componentDidMount() {
        try {
            const res = await getBackend2().manufacturing.getPlants({});
            const plants = res.plants;
            const line_groups = await this.getLineGroupsForDropdown(plants);
            this.setState({ plants_choice: plants, line_groups });
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
    }

    getLineGroupsForDropdown(plants: t.IPlantData[]): ILabelValuePairEx[] {
        const line_groups: ILabelValuePairEx[] = [];

        plants.forEach(plant => {
            const line_groups_for_user = BusinessLogic.getLineGroupsForUserPlant(plant.uuid);

            line_groups_for_user.forEach(lg => {
                if (plant) {
                    const label: string = `${plant.title} - ${lg.title}`;
                    line_groups.push({ label, value: lg.external_id, uuid: lg.uuid });
                }
            });
        });

        line_groups.sort((a, b) => a.label.localeCompare(b.label));

        return line_groups;
    }

    enableExternalIdEdit = () => {
        this.setState({ external_id_editing: true }, () => {
            this.external_id_input_ref.current?.focus();
        });
    };

    onExternalIdChange = (external_id: string, options?: { force?: boolean }) => {
        const trimmed = external_id.trim();
        if (trimmed === "") {
            this.invalidateExternalIdCheck();
            this.setState({ external_id_collisions: [], external_id_check_pending: false });
            return;
        }

        if (!options?.force && this.state.editFlag && trimmed === this.state.external_id_original.trim()) {
            this.invalidateExternalIdCheck();
            this.setState({ external_id_collisions: [], external_id_check_pending: false });
            return;
        }

        this.clearExternalIdCheckTimer();
        this.setState({ external_id_check_pending: true, external_id_collisions: [] });
        this.external_id_check_timer = setTimeout(() => {
            this.external_id_check_timer = null;
            void this.checkExternalIdCollision(trimmed);
        }, EXTERNAL_ID_CHECK_DEBOUNCE_MS);
    };

    async checkExternalIdCollision(external_id: string) {
        if (this.state.editFlag && this.state.uuid === "") {
            return;
        }

        const seq = ++this.external_id_check_seq;
        const trimmed_external_id = external_id.trim();
        try {
            const { people } = await getBackend2().manufacturing.getPeople({
                external_id: trimmed_external_id,
                include_inactive: true,
            });
            if (seq !== this.external_id_check_seq) {
                return;
            }
            if (trimmed_external_id !== this.state.external_id.trim()) {
                return;
            }

            const collisions = this.buildExternalIdCollisions(people, trimmed_external_id);

            this.setState({
                external_id_collisions: collisions,
                external_id_check_pending: false,
            });
        } catch (_err: unknown) {
            if (seq !== this.external_id_check_seq) {
                return;
            }
            if (trimmed_external_id !== this.state.external_id.trim()) {
                return;
            }
            this.setState({ external_id_check_pending: false, external_id_collisions: [] });
        }
    }

    getSelectedPlantUuids = (): string[] => {
        return this.state.plants.map(plant => plant.value);
    };

    buildExternalIdCollisions = (people: t.IPersonData[], external_id: string): ExternalIdCollision[] => {
        const trimmed_external_id = external_id.trim();
        return people
            .filter(person => person.external_id.trim() === trimmed_external_id)
            .filter(person => this.state.uuid === "" || person.uuid !== this.state.uuid)
            .map(person => ({
                uuid: person.uuid,
                name: person.name,
                plant_uuids: person.plant_uuids,
            }));
    };

    getVisibleExternalIdCollisions = (): ExternalIdCollision[] => {
        const plant_uuids = this.getSelectedPlantUuids();
        if (plant_uuids.length === 0) {
            return [];
        }
        return this.state.external_id_collisions.filter(collision => {
            return intersect(collision.plant_uuids, plant_uuids).length > 0;
        });
    };

    hasExternalIdPlantOverlap(): boolean {
        return this.getVisibleExternalIdCollisions().length > 0;
    }

    /**
     * Handling change of any input component.
     */
    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;
        let value: string | boolean = target.value;
        const name = target.name;

        // hack for toggle element
        if (name === "adjuster" || name === "active") {
            value = (target as HTMLInputElement).checked;
        }

        // update new datasource placeholder
        this.setState(prevState => ({
            ...prevState,
            [name]: value,
        }));

        if (name === "external_id") {
            this.onExternalIdChange(value as string);
        }
    };

    /**
     * Handling multi-select change.
     */
    handleMultiSelectChange = (data: any) => {
        this.setState({ plants: data }, () => {
            if (this.state.editFlag && this.state.uuid === "") {
                return;
            }
            if (this.state.external_id.trim() !== "") {
                this.onExternalIdChange(this.state.external_id, { force: true });
            }
        });
    };

    /**
     * Handling shift change.
     *
     * @param {array} data Array of preferred shifts (array of 3 booleans for 1st, 2nd and 3rd shift)
     */
    handleShiftChange = (data: boolean[]) => {
        this.setState({ shift_preference: data });
    };

    async handleSubmit() {
        if (this.isValidForm()) {
            try {
                const plant_uuids = this.state.plants.map(plant => plant.value);
                const data = await getBackend2().manufacturing.createPerson({
                    active: this.state.active,
                    adjuster: this.state.adjuster,
                    plant_uuids,
                    shift_preference: this.state.shift_preference,
                    ted_id: this.state.ted_id,
                    external_id: this.state.external_id.trim(),
                    name: this.state.name,
                    tags: this.state.tags,
                });
                this.props.history.push(`/digital-twin/resources/people/${data.uuid}`);
            } catch (err: unknown) {
                this.setState({ error: (err as Error).message });
            }
        }
    }

    handleCancel() {
        this.props.history.push("/digital-twin/resources/people");
    }

    handleLineGroupChange = (option: ILabelValuePair) => {
        this.setState(prevState => ({
            ...prevState,
            ted_id: option.value,
        }));
    };

    getSelectedLineGroup = (): ILabelValuePair | null => {
        if (this.state.ted_id) {
            return this.state.line_groups.find(lg => lg.value === this.state.ted_id);
        }

        return null;
    };

    isValidForm = (): boolean => {
        const trimmed_external_id = this.state.external_id.trim();
        const is_valid =
            !!this.state.ted_id && this.state.plants.length > 0 && !!this.state.name && trimmed_external_id !== "";

        if (!is_valid) {
            this.setState({
                error: translate(
                    "Manufacturing.LineGroups.cannot_submit_missing_values",
                    "Cannot submit a form with missing values"
                ),
            });
            window.scrollTo(0, 0);
            return false;
        }

        if (this.state.external_id_check_pending) {
            this.setState({
                error: translate(
                    "Manufacturing.People.external_id_check_pending_save_blocked",
                    "Cannot save while External ID availability is still being checked."
                ),
            });
            window.scrollTo(0, 0);
            return false;
        }

        if (this.hasExternalIdPlantOverlap()) {
            this.setState({
                error: translate(
                    "Manufacturing.People.external_id_collision_save_blocked",
                    "Cannot save: this External ID is already used in the same plant(s)."
                ),
            });
            window.scrollTo(0, 0);
            return false;
        }

        return true;
    };

    onChangeTag = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    renderCannotDeactivate = () => {
        if (!this.state.has_enabled_future_shifts) {
            return null;
        }

        return (
            <i
                className="fas fa-info-circle"
                style={{ color: "#b84747", fontSize: "25px" }}
                title={translate(
                    "common.cannot_deactivate",
                    "Some actions are not allowed, person still has enabled shifts"
                )}
            ></i>
        );
    };

    renderExternalIdCollisionLinks = (collisions: ExternalIdCollision[]) => {
        return collisions.map((collision, index) => (
            <React.Fragment key={collision.uuid}>
                {index > 0 && ", "}
                <Link to={`/digital-twin/resources/people/${collision.uuid}`}>{collision.name}</Link>
            </React.Fragment>
        ));
    };

    renderExternalIdCollisionWarning = () => {
        const { external_id_check_pending } = this.state;
        const visible_collisions = this.getVisibleExternalIdCollisions();

        if (external_id_check_pending) {
            return (
                <small className="text-muted d-block mt-1">
                    {translate("Manufacturing.People.external_id_checking", "Checking availability...")}
                </small>
            );
        }

        if (visible_collisions.length === 0) {
            return null;
        }

        const names = this.renderExternalIdCollisionLinks(visible_collisions);
        const message =
            visible_collisions.length === 1 ? (
                <FormattedMessage
                    id="Manufacturing.People.external_id_collision"
                    defaultMessage="Another person already uses this External ID: {name}."
                    values={{ name: names }}
                />
            ) : (
                <FormattedMessage
                    id="Manufacturing.People.external_id_collision_multiple"
                    defaultMessage="Other people already use this External ID: {names}."
                    values={{ names }}
                />
            );

        return (
            <div className="mt-1">
                <ErrorComponent msg={<div>{message}</div>} type="warning" />
            </div>
        );
    };

    renderExternalIdField = () => {
        const is_external_id_disabled = this.state.editFlag && !this.state.external_id_editing;

        return (
            <div className="form-group">
                <label>
                    <FormattedMessage id="common.external_id" defaultMessage="External ID" />:
                </label>
                <div className="d-flex align-items-center">
                    <input
                        ref={this.external_id_input_ref}
                        name="external_id"
                        type="text"
                        disabled={is_external_id_disabled}
                        className="form-control"
                        value={this.state.external_id}
                        onChange={this.handleInputChange}
                    />
                    {this.state.editFlag && is_external_id_disabled && (
                        <button
                            type="button"
                            className="btn btn-short btn-outline-secondary ml-2"
                            title={translate("Manufacturing.People.edit_external_id", "Edit External ID")}
                            aria-label={translate("Manufacturing.People.edit_external_id", "Edit External ID")}
                            onClick={this.enableExternalIdEdit}
                        >
                            <i className="far fa-edit" aria-hidden="true" />
                        </button>
                    )}
                </div>
                {this.renderExternalIdCollisionWarning()}
            </div>
        );
    };

    render() {
        const plant_options = this.state.plants_choice.map(plant => {
            return { value: plant.uuid, label: plant.title };
        });
        const tagCategories = mapDataToCategories(
            this.state.tags,
            PEOPLE_TAGS_EDITOR as TagsConfig,
            PEOPLE_TAGS_ACCESS
        );
        const data_nonstd = mapDataToCustomTags(this.state.tags, PEOPLE_TAGS_EDITOR as TagsConfig);

        let customStyles = {};
        if (this.state.has_enabled_future_shifts) {
            customStyles = { multiValueRemove: base => ({ ...base, display: "none" }) };
        }

        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <div className="d-flex justify-content-between">
                                        <h2 className="mb-4">{this.state.componentTitle}</h2>
                                        {this.renderCannotDeactivate()}
                                    </div>
                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <div className="tab-content">
                                        {this.renderExternalIdField()}

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.name" defaultMessage="Name" />:
                                            </label>
                                            <input
                                                name="name"
                                                type="text"
                                                className="form-control"
                                                value={this.state.name}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.active" defaultMessage="Active" />:
                                            </label>
                                            <br />
                                            <Toggle
                                                name="active"
                                                checked={this.state.active}
                                                disabled={this.state.active && this.state.has_enabled_future_shifts}
                                                onChange={this.handleInputChange}
                                            />
                                            <br />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.adjuster" defaultMessage="Adjuster" />:
                                            </label>
                                            <br />
                                            <Toggle
                                                name="adjuster"
                                                checked={this.state.adjuster}
                                                onChange={this.handleInputChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.linegroup" defaultMessage="Line-group" />:
                                            </label>
                                            <Select
                                                name="ted_id"
                                                value={this.getSelectedLineGroup()}
                                                options={this.state.line_groups}
                                                isSearchable
                                                onChange={this.handleLineGroupChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage
                                                    id="Manufacturing.People.plant_membership"
                                                    defaultMessage="Plant membership"
                                                />
                                                :
                                            </label>
                                            <Select
                                                name="plants"
                                                value={this.state.plants}
                                                options={plant_options}
                                                isMulti
                                                styles={customStyles}
                                                isClearable={!this.state.has_enabled_future_shifts}
                                                onChange={this.handleMultiSelectChange}
                                            />
                                        </div>

                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage
                                                    id="Manufacturing.People.allowed_shifts"
                                                    defaultMessage="Allowed shifts"
                                                />
                                                :
                                            </label>
                                            <DayShiftsToggle
                                                selectedShifts={this.state.shift_preference}
                                                onSelectedShiftsChange={this.handleShiftChange}
                                            />
                                        </div>
                                    </div>

                                    <h2 className="mb-4">
                                        <FormattedMessage id="common.standard_data" defaultMessage="Standard data" />
                                    </h2>
                                    <div className="text-right mb-3">
                                        <UserDocumentationLink link_key={"people"} />
                                    </div>

                                    <TagsTables
                                        categories={tagCategories}
                                        columns={Columns}
                                        onChangeCell={this.onChangeTag}
                                        customTags={data_nonstd}
                                    />

                                    <div className="button-list button-list-entity-edit order-last ml-auto">
                                        <button className="btn btn-primary" onClick={() => this.handleSubmit()}>
                                            <FormattedMessage id="common.save" defaultMessage="Save" />
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => this.handleCancel()}
                                        >
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>

                                    <ErrorComponent msg={this.state.success} type="success" />
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default PeopleCreate;
